import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import ReactLoading from 'react-loading';
import { Services } from 'Services';
import { Success, Error } from 'Util/Toast';
import Layout from 'Components/Layout';
import { Helmet } from 'react-helmet';
import { BsCloudUpload } from 'react-icons/bs';
import CommonTutorClasses from '../commoncssfiles/CategoryTutor.module.css';
import UserClasses from '../users/Users.module.css';
import CategoryClasses from './AddCategory.module.css';
import ProductClasses from '../products/Products.module.css';

const EditCategory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { register, handleSubmit, setValue, formState: { errors } } = useForm();
    const [imagePreview, setImagePreview] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState('');

    // Retrieve the category object from location state
    const { category } = location.state || {};

    // Use useEffect to prefill form fields and image preview when category changes
    useEffect(() => {
        if (category) {
            setValue('CategoryName', category.categoryName);
            if (category.imageUrl) {
                setImagePreview(category.imageUrl); // Set initial image preview
                const endpoint = category.imageUrl.split('/').pop(); // Extract endpoint from URL
                setValue('imageUrl', endpoint); // Set only the endpoint in the form state
                setUploadedFileName(endpoint); // Set the uploaded file name for validation
            }
        }
    }, [category, setValue]);

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            Error(`${file.name} is not a valid image format.`);
            return;
        }

        let formData = new FormData();
        formData.append("image", file);
        formData.append("folder", "categories");

        try {
            setLoading(true);
            const res = await Services.UploadImage(formData);
            if (res?.Status === 1) {
                const uploadedUrl = res?.ImageURL;
                setUploadedFileName(res?.ImageID);
                setImagePreview(uploadedUrl || URL.createObjectURL(file));
                setValue('imageUrl', res?.ImageID);
                setLoading(false);
            } else {
                Error(res?.Message || "Upload failed.");
                setLoading(false);
            }
        } catch (err) {
            console.error("Upload Error:", err);
            Error("Upload error. Please try again.");
            setLoading(false);
        }
    };

    const onSubmit = async (data) => {
        console.log('Form submitted with data:', data);
        console.log('Uploaded file name:', uploadedFileName);
        
        if (!uploadedFileName) {
            Error('Please upload an image.');
            return;
        }

        setLoading(true);

        const requestBody = {
            categoryName: data.CategoryName,
            imageUrl: uploadedFileName, // Use the uploaded file name
            priority: 1, // Set default priority to 1
        };

        console.log('Request body:', requestBody);

        const token = { Authorization: `token ${localStorage.getItem('Token')}` };

        try {
            const response = await Services.updateCategory(category.categoryId, requestBody, token);
            console.log('API Response:', response);
            
            if (response.Status === 1) {
                navigate('/categories');
                Success(response.Message);
                console.log('Category updated successfully:', response.Message);
            } else {
                Error(response.Message);
                console.error('Error updating category:', response.Message);
            }
        } catch (error) {
            console.error('Error updating category:', error);
            Error('Error updating category');
        } finally {
            setLoading(false);
        }
    };

    const fileuploadDesign = (
        <div className={CommonTutorClasses["add-item"]} onClick={() => document.getElementById("image").click()}>
            <div className={CommonTutorClasses['upload-image']}>
                {imagePreview && <img src={imagePreview} alt="Selected" className={CommonTutorClasses['uploaded-coupon-image']} />}
                {loading && <ReactLoading className={ProductClasses['loading-spinner-container']} type="spin" color="#000" height={50} width={50} />}
                {!imagePreview && (
                    <div className={CommonTutorClasses['add-item']}>
                        <BsCloudUpload className={CommonTutorClasses['upload-icon']} />
                        <h5>Drag Your Files here</h5>
                        <p>(Only *.jpeg, *.png, *.webp will be accepted)</p>
                    </div>
                )}
                <input type="file" id="image" onChange={handleImageUpload} style={{ display: 'none' }} />
            </div>
        </div>
    );

    return (
        <Layout Active={'Categories'}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Update Category</title>
            </Helmet>
            <div className={UserClasses['main-first-container']}>
                <div className={UserClasses['main-data-tab']}>
                    <div className={UserClasses['add-users-btn']}>
                        <Button variant="contained" color="primary" onClick={() => navigate(-1)}>
                            BACK
                        </Button>
                    </div>
                    <div className={CommonTutorClasses['wrapper']} data-aos="zoom-in-down">
                        <h2 className={CommonTutorClasses['main-header']}>Update Category</h2>
                        <form style={{display: 'block'}} className={CommonTutorClasses["form"]} onSubmit={handleSubmit(onSubmit)}>
                            <div style={{marginBottom: '10px'}} className={CommonTutorClasses['form-control']}>
                                <label htmlFor="CategoryName">Category Name</label>
                                <input 
                                    style={{width: '97%'}} 
                                    type='text' 
                                    id="CategoryName"
                                    {...register('CategoryName', { required: 'Category name is required' })} 
                                />
                                {errors.CategoryName && <p className={CategoryClasses["error-text"]}>{errors.CategoryName.message}</p>}
                            </div>
                            <div className={CommonTutorClasses['file-upload-wrapper']}>
                                <header>
                                    <h3>
                                        Upload Category Image <span className="important">*</span>
                                    </h3>
                                </header>
                                <div className={CommonTutorClasses["upload-image"]}>
                                    {fileuploadDesign}
                                </div>
                                {!uploadedFileName && <p className="error-message">Please upload a category image.</p>}
                            </div>
                            <div className={ProductClasses['form-btn-main']}>
                                <Button 
                                    style={{marginTop: '10px'}} 
                                    type="submit" 
                                    variant="contained"
                                    disabled={loading}
                                    onClick={(e) => {
                                        console.log('Button clicked');
                                        // Let the form handle the submission
                                    }}
                                >
                                    {loading ? 'Updating Category...' : 'Update Category'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default EditCategory;